import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
} from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { CookieService } from 'ngx-cookie';
import { Observable } from 'rxjs/Observable';
import { ReplaySubject } from 'rxjs/ReplaySubject';
import { DATASTORE_CONFIG } from '../datastore/datastore.config';
import { DatastoreConfig } from '../datastore/datastore.interface';
import { AUTH_CONFIG } from './auth.config';
import { AuthConfig } from './auth.interface';

export interface AuthState {
  userId: string;
  token: string;
}

export interface SwitchAccountResponse {
  status: string;
  result: {
    token: string;
    user: number;
  };
}

export interface NavigationSwitchAccountResponse {
  status: string;
  result: {
    gotoUrl?: string;
  };
}

@Injectable()
export class Auth {
  private _authState: ReplaySubject<AuthState | undefined>;

  get authState(): Observable<AuthState | undefined> {
    return this._authState.asObservable();
  }

  // TODO: we should provide those to CookieModule.forRoot but it's tricky
  // because of AoT, best is probably to extend `ngx-cookie`
  private authCookiesExpires = new Date(
    new Date().setMonth(new Date().getMonth() + 1),
  );

  constructor(
    @Inject(AUTH_CONFIG) private authConfig: AuthConfig,
    @Inject(DATASTORE_CONFIG) private datastoreConfig: DatastoreConfig,
    private cookies: CookieService,
    private http: HttpClient,
  ) {
    this._authState = new ReplaySubject(1);
    // read session from cookie if any
    const userId = this.cookies.get(this.authConfig.userIdCookie);
    const token = this.cookies.get(this.authConfig.authHashCookie);
    if (userId && token) {
      this._authState.next({ userId, token });
    } else {
      this._authState.next(undefined);
    }
    // keep auth cookies in sync with session
    this._authState.skip(1).subscribe(s => {
      if (s) {
        this.cookies.put(this.authConfig.userIdCookie, s.userId, {
          expires: this.authCookiesExpires,
        });
        this.cookies.put(this.authConfig.authHashCookie, s.token, {
          expires: this.authCookiesExpires,
        });
      } else {
        this.cookies.remove(this.authConfig.userIdCookie);
        this.cookies.remove(this.authConfig.authHashCookie);
      }
    });
  }

  getUserId(): Observable<string> {
    // TODO: use Optional Chaining when available
    return this._authState
      .asObservable()
      .switchMap(
        auth => (auth ? Observable.of(auth.userId) : Observable.never()),
      );
  }

  getAuthorizationHeader(): Observable<HttpHeaders> {
    return this.authState.map(auth => {
      if (!auth) {
        return new HttpHeaders();
      }
      return new HttpHeaders().set(
        'freelancer-auth-v2',
        `${auth.userId};${auth.token}`,
      );
    });
  }

  setSession(userId: string, token: string) {
    this._authState.next({ userId, token });
  }

  deleteSession() {
    this._authState.next(undefined);
  }

  // FIXME T38832: This needs 2 AJAX calls because GAF.
  switchUser(newUserId: string): Promise<string | undefined> {
    return this.authState
      .take(1)
      .switchMap(auth => {
        if (!auth) {
          throw new Error('no user to switch account from');
        }
        const body = new FormData();
        body.append('user', auth.userId);
        body.append('other_user', newUserId);
        body.append('token', auth.token);

        return this.http
          .post<SwitchAccountResponse>(
            `${this.authConfig.baseUrl}/switch_account/`,
            body,
          )
          .map(response => response.result.token);
      })
      .withLatestFrom(this.getAuthorizationHeader())
      .switchMap(([newUserToken, authHeader]) => {
        const body = new FormData();
        body.append('user', newUserId);
        body.append('token', newUserToken);
        return this.http
          .post<NavigationSwitchAccountResponse>(
            `${this.datastoreConfig.ajaxBaseUrl}/navigation/switchaccount.php`,
            body,
            {
              headers: authHeader,
            },
          )
          .map(response => ({ response, newUserToken }));
      })
      .do(({ newUserToken }) =>
        this._authState.next({ userId: newUserId, token: newUserToken }),
      )
      .map(({ response }) => response.result.gotoUrl)
      .toPromise();
  }

  logout(): Promise<undefined> {
    return this.authState
      .take(1)
      .switchMap(auth => {
        if (!auth) {
          throw new Error('no user to logout');
        }
        const body = new FormData();
        body.append('user', auth.userId);
        body.append('token', auth.token);
        return this.http.post(`${this.authConfig.baseUrl}/logout/`, body);
      })
      .do(() => this._authState.next(undefined))
      .mapTo(undefined)
      .toPromise()
      .catch((err: HttpErrorResponse) => {
        if (err.error instanceof Error) {
          // A client-side or network error occurred.
          throw err.error;
        } else {
          // The backend returned an unsuccessful response code.
          throw new Error(err.message);
        }
      });
  }
}



// WEBPACK FOOTER //
// /mnt/jenkins-slave-workspace/workspace/gaf-build-i18n-webapp-compat/LANG/it/webapp/src/app/@shared/auth/auth.service.ts